import {
   AdminCrmPipelineFeature,
   CrmAccountFeature,
   CrmAccountFeatureEnum,
   CrmPipelineFeature,
   CrmPipelineFeatureEnum,
   FailedCallType,
   OutBoundCallType,
   PluginType as GraphqlPluginType,
   User as UserFromGraphql,
   UserGoogleAuthConsentStatus,
   UserPermission,
   IdentificationType
} from '@flowwai-frontend/bps-officer-web/graphql'
import { User } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { UserGoogleCalenderAuthConsentStatusType } from '@flowwai-frontend/bps-officer-web/common/data-access'

import { UserPermissionsModel } from '../stores/models/UserPermissionsModel/UserPermissionsModel'
import {
   AccountFeaturesStatusType,
   CallTypeEnum,
   FailedCallTypeEnum,
   PipelineFeaturesStatusType,
   PluginType
} from '../types'

export const convertToUserType = (user: UserFromGraphql): User => ({
   userId: user.userId,
   name: user.name,
   userName: user.username,
   email: user.email,
   profilePicUrl: user.profilePicUrl,
   signatureImgUrl: user.signatureImgLink,
   isAdmin: user.isAdmin,
   phoneNumber: {
      phoneNumber: user.phoneNumber?.phoneNumber || '',
      dialCode: user.phoneNumber?.dialCode || '',
      iso2CountryCode: user.phoneNumber?.iso2CountryCode || ''
   },
   attendance: user.attendance
})

export const updateUserPermissions = (
   user: UserFromGraphql,
   userPermissions: UserPermissionsModel
): UserPermissionsModel => {
   if (user.permissions.includes(UserPermission.WebAppAccess))
      userPermissions.enableAccessForWebApp()
   if (user.permissions.includes(UserPermission.CreateBoard)) {
      userPermissions.enableCreateBoard()
      userPermissions.enableReorderBoard()
   }
   if (user.permissions.includes(UserPermission.MobileAppAccess))
      userPermissions.enableAccessForMobileApp()

   return userPermissions
}

export const convertOutboundCallTypeToCallTypeEnum = (
   callType: OutBoundCallType
): CallTypeEnum => {
   switch (callType) {
      case OutBoundCallType.CloudCalling:
         return CallTypeEnum.CLOUD_CALLING
      case OutBoundCallType.DirectCalling:
         return CallTypeEnum.DIRECT_CALLING
      case OutBoundCallType.NotAllowed:
         return CallTypeEnum.NOT_ALLOWED
   }
}

export const convertGraphqlFailedCallTypeToFailedCallTypeEnum = (
   callType: FailedCallType
): FailedCallTypeEnum => {
   switch (callType) {
      case FailedCallType.AnyError:
         return FailedCallTypeEnum.ANY_ERROR
      case FailedCallType.OnlyDnd:
         return FailedCallTypeEnum.ONLY_DND
   }
}

export const convertGraphqlPluginTypeToLocal = (
   plugin: GraphqlPluginType
): PluginType | null => {
   switch (plugin) {
      case GraphqlPluginType.Knowlarity:
         return 'KNOWLARITY'
      case GraphqlPluginType.Wati:
         return 'WATI'
      case GraphqlPluginType.Gallabox:
         return 'GALLABOX'
      case GraphqlPluginType.Gupshup:
         return 'GUPSHUP'
      case GraphqlPluginType.Smartflo:
         return 'SMARTFLO'
      default:
         return null
   }
}

export const getFilteredActivePlugins = (
   plugins: GraphqlPluginType[]
): PluginType[] => {
   const filteredActivePlugins: PluginType[] = []

   plugins.forEach(plugin => {
      const pluginType = convertGraphqlPluginTypeToLocal(plugin)
      if (pluginType) filteredActivePlugins.push(pluginType)
   })

   return filteredActivePlugins
}

export const convertUserGoogleAuthConsentStatusToUserGoogleCalenderAuthConsentStatusType = (
   status: UserGoogleAuthConsentStatus
): UserGoogleCalenderAuthConsentStatusType => {
   switch (status) {
      case UserGoogleAuthConsentStatus.Given:
         return 'AUTHORIZATION_GRANTED'
      case UserGoogleAuthConsentStatus.NotGiven:
         return 'AUTHORIZATION_NOT_GRANTED'
   }
}

export const getPipelineFeaturesStatus = (
   features:
      | Array<CrmPipelineFeature>
      | Array<AdminCrmPipelineFeature>
      | undefined
): PipelineFeaturesStatusType => {
   let isActivitiesEnabled = true
   let isCallLogsEnabled = true
   let isSendWhatsAppMessagesEnabled = true
   let isShareableResourcesEnabled = true
   let isTasksEnabled = true
   const isRecordScoreEnabled = true

   features?.forEach(feature => {
      // TODO: update this for Record Score
      switch (feature.feature) {
         case CrmPipelineFeatureEnum.Activities: {
            isActivitiesEnabled = !feature.isDisabled
            break
         }

         case CrmPipelineFeatureEnum.Calls: {
            isCallLogsEnabled = !feature.isDisabled
            break
         }

         case CrmPipelineFeatureEnum.ManualWhatsappMsgs: {
            isSendWhatsAppMessagesEnabled = !feature.isDisabled
            break
         }

         case CrmPipelineFeatureEnum.ShareableResources: {
            isShareableResourcesEnabled = !feature.isDisabled
            break
         }

         case CrmPipelineFeatureEnum.Tasks: {
            isTasksEnabled = !feature.isDisabled
            break
         }

         default:
            break
      }
   })

   return {
      isActivitiesEnabled,
      isCallLogsEnabled,
      isSendWhatsAppMessagesEnabled,
      isShareableResourcesEnabled,
      isTasksEnabled,
      isRecordScoreEnabled
   }
}

export const getAccountFeaturesStatus = (
   accountFeatures: Array<CrmAccountFeature>
): AccountFeaturesStatusType => {
   let isAIChatSupportEnabled = false
   let isContactsEnabled = true
   let isDataCoPilotEnabled = false
   let isSchedulingEnabled = false
   let isGoogleCalendarIntegrationEnabled = false
   let isFlowwSignEnabled = false
   let isDocuSignEnabled = false
   let isAttendanceTrackingEnabled = false
   let isOfficerManagementEnabled = false
   let isApplicationManagementEnabled = false
   let isTdrManagementEnabled = false
   let isScrutinyKeyGenerationEnabled = false

   accountFeatures.forEach(accountFeature => {
      switch (accountFeature.feature) {
         case CrmAccountFeatureEnum.Contacts: {
            isContactsEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.Scheduling: {
            isSchedulingEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.GoogleCalendarIntegration: {
            isGoogleCalendarIntegrationEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.FlowwSign: {
            isFlowwSignEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.Docusign: {
            isDocuSignEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.AttendanceTracking: {
            isAttendanceTrackingEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.AiChatSupport: {
            isAIChatSupportEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.DataCopilot: {
            isDataCoPilotEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.OfficerManagement: {
            isOfficerManagementEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.ApplicationManagement: {
            isApplicationManagementEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.TdrManagement: {
            isTdrManagementEnabled = !accountFeature.isDisabled
            break
         }
         case CrmAccountFeatureEnum.ScrutinyKeyGeneration: {
            isScrutinyKeyGenerationEnabled = !accountFeature.isDisabled
            break
         }
         default:
            break
      }
   })

   return {
      isAIChatSupportEnabled,
      isContactsEnabled,
      isDataCoPilotEnabled,
      isSchedulingEnabled,
      isGoogleCalendarIntegrationEnabled,
      isFlowwSignEnabled,
      isDocuSignEnabled,
      isAttendanceTrackingEnabled,
      isOfficerManagementEnabled,
      isApplicationManagementEnabled,
      isTdrManagementEnabled,
      isScrutinyKeyGenerationEnabled
   }
}

export const getUserRoleIdsForGqlIdentifications = (
   user: UserFromGraphql
): Array<string> => {
   const userRoleIds: Array<string> = []

   user.identifications.forEach(identification => {
      const isRoleConfig =
         identification.identificationType === IdentificationType.Role

      if (isRoleConfig) {
         userRoleIds.push(identification.identificationId)
      }
   })

   return userRoleIds
}

export const getUserRoleNamesForGqlIdentifications = (
   user: UserFromGraphql
): Array<string> => {
   const userRoleNames: Array<string> = []

   user.identifications.forEach(identification => {
      const isRoleConfig =
         identification.identificationType === IdentificationType.Role

      if (isRoleConfig) {
         userRoleNames.push(identification.name)
      }
   })

   return userRoleNames
}
